import { Thumb, Root as ToggleRoot } from "@radix-ui/react-switch";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "../lib/utils";
import { Icon } from "./icon";

interface ToggleProps extends ComponentPropsWithoutRef<typeof ToggleRoot> {
  label?: string;
}

interface ToggleComponentProps extends ToggleProps {
  ref?: React.Ref<HTMLButtonElement>;
}

const Toggle: React.FC<ToggleComponentProps> = ({
  className,
  label,
  defaultChecked,
  checked,
  ref,
  ...props
}) => {
  const id = props.id || `switch-${Math.random().toString(36).substr(2, 9)}`;
  const toggleElement = (
    <ToggleRoot
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center transition-colors focus-visible:outline-none disabled:cursor-not-allowed",
        "h-4 w-6 px-[2px] rounded-custom",
        checked ? "bg-primary" : "bg-gray-500",
        "disabled:!bg-gray-100",
        className,
      )}
      defaultChecked={defaultChecked}
      checked={checked}
      {...props}
      ref={ref}
      id={id}
    >
      <Thumb
        className={cn(
          "pointer-events-none block ring-0 transition-transform",
          "w-3 h-3 shadow-sm block pointer-events-none rounded-custom transition-transform bg-white",
          checked ? "translate-x-4" : "translate-x-0",
          "flex items-center justify-center",
        )}
      >
        {checked && !props.disabled && (
          <Icon icon={"check"} className="text-pink-500" size={16} />
        )}
      </Thumb>
    </ToggleRoot>
  );

  return label ? (
    <label htmlFor={id} className="flex items-center space-x-2 cursor-pointer">
      {toggleElement}
      <span className="font-title text-gray-900">{label}</span>
    </label>
  ) : (
    toggleElement
  );
};

Toggle.displayName = "Toggle";

export { Toggle };
